import { getRoutePermissionConfig } from '@/config/routePermissions';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { Navigate } from 'react-router-dom';

interface RoutePermissionGuardProps {
  children: React.ReactNode;
}

const RouteGuardSpinner = () => null;

/**
 * Guard de permissions au niveau route.
 * Lit le pathname actuel, trouve la config dans routePermissions,
 * vérifie hasPermission / requireSuperAdmin et redirige vers HOME si refus.
 * Super admin bypass toutes les vérifications.
 * Après un reload, attend que les permissions soient chargées (évite redirection à la racine).
 */
const RoutePermissionGuard: React.FC<RoutePermissionGuardProps> = ({ children }) => {
  const location = useLocation();
  const { user, isLoading: authLoading } = useAuth();
  const { hasPermission, isSuperAdmin, isLoading, permissions } = usePermissions();
  const pathname = location.pathname;

  const config = getRoutePermissionConfig(pathname);

  if (authLoading || isLoading) {
    return <RouteGuardSpinner />;
  }

  // Après reload : utilisateur connecté mais permissions pas encore rechargées (liste vide) → attendre
  if (
    user &&
    config?.requiredPermissions &&
    config.requiredPermissions.length > 0 &&
    permissions.length === 0
  ) {
    return <RouteGuardSpinner />;
  }

  // Route super admin : attendre que isSuperAdmin soit à jour (évite redirection au reload avant que PermissionsContext ait traité le user)
  if (config?.requireSuperAdmin && user && !isSuperAdmin && permissions.length === 0) {
    return <RouteGuardSpinner />;
  }

  if (!config || isSuperAdmin) {
    return <>{children}</>;
  }

  if (config.requireSuperAdmin && !isSuperAdmin) {
    if (import.meta.env.DEV) {
      console.debug('[RoutePermissionGuard] Accès refusé (super admin requis)', { pathname });
    }
    return <Navigate to={ROUTES.HOME} replace />;
  }

  if (config.requiredPermissions && config.requiredPermissions.length > 0) {
    const hasAny = config.requiredPermissions.some(key => hasPermission(key));
    if (!hasAny) {
      if (import.meta.env.DEV) {
        console.debug('[RoutePermissionGuard] Accès refusé (permissions insuffisantes)', {
          pathname,
          required: config.requiredPermissions,
        });
      }
      return <Navigate to={ROUTES.HOME} replace />;
    }
  }

  return <>{children}</>;
};

export default RoutePermissionGuard;
